import styled from '@emotion/styled';
import { useFormikContext } from 'formik';
import { ReactNode, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import { Element, scroller } from 'react-scroll';
import { StyledForm } from '../index';
import EnableField from '../EnableField';
import NameField from '../NameField';
import TermOfValidityField from '../TermOfValidityField';
import Nav, { linkProps } from './Nav';
import Regions from './Regions';
import RegionExecuteContextsField from './RegionExecuteContextsField';
import OrderStatusField from '../OrderStatusField';
import { FV, getFormRegions } from '../../NewPolicy/share';
import { useReactScrollContext } from 'views/Aide/components/ReactScrollContext';

export const NoRegionContainer = styled.div`
  position: relative;
`;

export const NoRegionWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  > nav {
    position: sticky;
    top: 56px;
    flex: 0 0 auto;
    margin-top: 16px;
    margin-right: 16px;
    background: var(--fett-bg-panel);
  }
  > section {
    flex: 1 0 0;
    > form > div {
      background: var(--fett-bg-panel);
      &:not(:last-of-type) {
        margin-bottom: 16px;
      }
    }
  }
`;

export interface NoRegionFormContentProps {
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
  header: ReactNode;
  footer: ReactNode;
  replyOption?: Marketing.ReplyOption;
  readOnly?: boolean;
  defaultActiveRegion?: General.UpperCaseRegion;
  taskFields?: ReactNode;
}

function NoRegionFormContent(props: NoRegionFormContentProps) {
  const {
    scenarioType,
    platform,
    header,
    footer,
    replyOption,
    readOnly,
    defaultActiveRegion,
    taskFields,
  } = props;

  const { t } = useTranslate();

  const {
    values: { regions = [], regionStoreList },
    setValues,
  } = useFormikContext<FV>();

  const { containerId, basicId, createRegionContextId } = useReactScrollContext();

  useEffect(() => {
    if (!regionStoreList) {
      return;
    }

    getFormRegions(scenarioType, platform, regionStoreList).then((regions) => {
      setValues((pre) => ({
        ...pre,
        regions,
        regionExecuteContexts: pre.regionExecuteContexts?.map(({ enable, region, ...others }) => {
          return {
            enable: regions.includes(region) && enable,
            region,
            ...others,
          };
        }),
      }));
    });
  }, [platform, scenarioType, regionStoreList, setValues]);

  const supportBasic = scenarioType !== 'RECEPTION_SCORE' && scenarioType !== 'CSAT_INVITE_REVIEW';

  const nav = (
    <Nav<FV>
      supportBasic={supportBasic}
      basicValidate={(errors) => {
        return (
          !errors.enable &&
          !errors.name &&
          !errors.termOfValidity &&
          !errors.regions &&
          !errors.regionStoreList
        );
      }}
    />
  );

  useEffect(() => {
    if (defaultActiveRegion) {
      const action = () => {
        scroller.scrollTo(createRegionContextId(defaultActiveRegion), {
          ...linkProps,
          containerId,
          duration: 0,
        });
      };

      if (requestAnimationFrame) {
        requestAnimationFrame(action);
      } else {
        action();
      }
    }
  }, [defaultActiveRegion, containerId, createRegionContextId]);

  const [
    storesField,
    timeoutReplyField,
    welcomeReplyField,
    filterRuleField,
    inviteFollowerField,
    filterAfterSaleField,
    praiseTriggerPointField,
    roundConfigField,
    conflict,
    knowledgeTips,
  ] = (taskFields as ReactNode[]) || [];

  return (
    <NoRegionContainer>
      {header}
      <NoRegionWrapper>
        {nav}
        <section>
          <StyledForm>
            {supportBasic && (
              <Element name={basicId}>
                {knowledgeTips}
                <h3>{t('aide.基础配置')}</h3>
                <NameField disabled={readOnly} />
                <EnableField disabled={readOnly} />
                {storesField}
                <TermOfValidityField disabled={readOnly} scenarioType={scenarioType} />
                <OrderStatusField disabled={readOnly} scenarioType={scenarioType} />
                {filterRuleField}
                {timeoutReplyField}
                {welcomeReplyField}
                {inviteFollowerField}
                {filterAfterSaleField}
                {praiseTriggerPointField}
                {roundConfigField}
                {conflict}
                {regions.length > 0 && <Regions disabled={readOnly} />}
              </Element>
            )}
            <RegionExecuteContextsField
              scenarioType={scenarioType}
              platform={platform}
              replyOption={replyOption}
              disabled={readOnly}
            />
          </StyledForm>
          {footer}
        </section>
      </NoRegionWrapper>
    </NoRegionContainer>
  );
}

export default NoRegionFormContent;
